import { useEffect, useRef, useState } from 'react';
import { io, type Socket } from 'socket.io-client';
import type { LiveState } from './types';

/**
 * Lebende Verbindung zum Server.
 *
 * Der Server schiebt nach jeder Änderung den neuen Stand. Die Oberfläche rechnet selbst nichts aus
 * – sie zeigt, was ankommt. Dadurch sehen Spieler, Verwaltung und Overlay immer dasselbe Spiel,
 * und keine Anzeige kann vom wirklichen Stand abweichen.
 */
export type Verbindungsstand = 'verbindet' | 'live' | 'getrennt' | 'fehler';

export interface Live<T extends LiveState> {
  state: T | null;
  status: Verbindungsstand;
  fehler: string | null;
  /** Für Spieler, die mehreren Teams angehören. */
  wechsleTeam: (teamId: string) => void;
}

export function useLive<T extends LiveState>(
  auth: { lobbyId?: string; overlayKey?: string; teamId?: string },
): Live<T> {
  const [state, setState] = useState<T | null>(null);
  const [status, setStatus] = useState<Verbindungsstand>('verbindet');
  const [fehler, setFehler] = useState<string | null>(null);
  const socketRef = useRef<Socket | null>(null);

  const schlüssel = JSON.stringify(auth);

  useEffect(() => {
    const socket = io({ auth: JSON.parse(schlüssel) as Record<string, unknown> });
    socketRef.current = socket;

    socket.on('connect', () => setStatus('live'));
    socket.on('state', (daten: T) => {
      setState(daten);
      setStatus('live');
      setFehler(null);
    });
    socket.on('error', (meldung: { code: string; message: string }) => {
      setFehler(meldung.message);
      setStatus('fehler');
    });
    socket.on('disconnect', () => setStatus((bisher) => (bisher === 'fehler' ? bisher : 'getrennt')));

    return () => {
      socket.close();
      socketRef.current = null;
    };
  }, [schlüssel]);

  return {
    state,
    status,
    fehler,
    wechsleTeam: (teamId: string) => socketRef.current?.emit('team:switch', teamId),
  };
}
